/* Copyright (c) 2026 Xperi. All Rights Reserved. */

// Imported from the concrete module rather than the `../logging` barrel: this
// runs on the earliest boot path (an `import()` can fail before Sage is
// initialised), same reasoning as clearAppStorage.
import { Log } from "../logging/Log";
import { reloadApplicationAtRoot } from "./reloadApplicationAtRoot";
import {
  canAttemptSilentReload,
  recordSilentReloadAttempt
} from "./silentReloadGuard";
import { canAttemptRestart, recordRestartAttempt } from "./restartLimitGuard";

const TAG: string = "chunkLoadRecovery";

/**
 * Recovery for a failed code-split chunk download.
 *
 * Every screen is behind a route-level `import()`, so a chunk that cannot be
 * fetched — a redeploy that replaced the hashed filenames under a running app,
 * a dropped Wi-Fi frame mid-download, a CDN blip — leaves the app with no
 * screen to render. Webpack surfaces it as a `ChunkLoadError`.
 *
 * The common cause is a redeploy under a running app: release builds name
 * chunks `[name].[contenthash].chunk.js`, so the files the loaded `index.html`
 * points at stop existing the moment a new build lands. A long-lived session —
 * the >48h longevity runs in UAW-2855 — is almost certain to span one. A
 * restart fetches the new `index.html`, which references the chunks that do
 * exist, so the first failure recovers silently.
 * {@link canAttemptSilentReload} keeps that from looping.
 */

/** Signature used when the error carries nothing identifying. */
const UNKNOWN_CHUNK_SIGNATURE: string = "unknown-chunk";

/** Longest error message kept as a signature, to bound what we persist. */
const MAX_SIGNATURE_LENGTH: number = 120;

/**
 * Message shapes webpack (and native ESM, on browsers that use it) produce for
 * a chunk that could not be downloaded or executed.
 */
const CHUNK_LOAD_ERROR_PATTERNS: RegExp[] = [
  /loading chunk \S+ failed/i,
  /loading css chunk \S+ failed/i,
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i
];

/** Extracts the chunk name out of "Loading chunk <name> failed." */
const CHUNK_NAME_PATTERN: RegExp = /chunk (\S+) failed/i;

/** Set once `installChunkLoadRecovery` has registered its listener. */
let isRecoveryInstalled: boolean = false;

function getErrorMessage(error: unknown): string {
  if (error === null || error === undefined) {
    return "";
  }
  const candidate = error as { message?: unknown };
  if (typeof candidate.message === "string") {
    return candidate.message;
  }
  if (typeof error === "string") {
    return error;
  }
  return "";
}

/**
 * Whether `error` is a failed chunk download rather than an application bug.
 *
 * Matched on `name`/`code` first — webpack tags its own failures — and on the
 * message only as a fallback, because the tag is lost when the error crosses a
 * boundary that re-wraps it.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (error === null || error === undefined) {
    return false;
  }
  const candidate = error as { name?: unknown; code?: unknown };
  if (candidate.name === "ChunkLoadError") {
    return true;
  }
  if (candidate.code === "CSS_CHUNK_LOAD_FAILED") {
    return true;
  }
  const message: string = getErrorMessage(error);
  if (message.length === 0) {
    return false;
  }
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern: RegExp): boolean =>
    pattern.test(message)
  );
}

/**
 * Stable identifier for the failure, so a *different* chunk failing later still
 * gets its own silent reload instead of inheriting this one's cooldown.
 */
export function getChunkLoadErrorSignature(error: unknown): string {
  const message: string = getErrorMessage(error);
  const match: RegExpExecArray | null = CHUNK_NAME_PATTERN.exec(message);
  if (match !== null) {
    return match[1];
  }
  if (message.length === 0) {
    return UNKNOWN_CHUNK_SIGNATURE;
  }
  return message.slice(0, MAX_SIGNATURE_LENGTH);
}

/** Whether this chunk failure has a silent reload left to spend. */
export function canAttemptSilentChunkReload(error: unknown): boolean {
  return (
    canAttemptSilentReload(getChunkLoadErrorSignature(error)) &&
    canAttemptRestart()
  );
}

/**
 * Records the attempt and restarts the app at `/`. Returns false — without
 * restarting — when the failure is not a chunk load error, when the cooldown
 * still blocks it, when the session has already spent its silent-restart
 * budget (see `restartLimitGuard`), or when the attempt could not be
 * persisted; the caller then surfaces the error.
 */
export function attemptSilentChunkReload(error: unknown): boolean {
  if (!isChunkLoadError(error)) {
    return false;
  }
  const signature: string = getChunkLoadErrorSignature(error);
  if (!canAttemptSilentReload(signature)) {
    Log.w(TAG, "Silent reload refused for chunk: " + signature);
    return false;
  }
  if (!canAttemptRestart()) {
    Log.w(TAG, "Silent reload refused — restart limit reached");
    return false;
  }
  if (!recordSilentReloadAttempt(signature)) {
    return false;
  }
  if (!recordRestartAttempt()) {
    return false;
  }
  Log.i(TAG, "Silent restart for failed chunk: " + signature);
  reloadApplicationAtRoot();
  return true;
}

/**
 * Covers the chunk failures no route error boundary can see: an `import()` that
 * is not awaited by a route — a preload, a lazily loaded locale file, an
 * on-demand import inside a component — rejects into
 * `unhandledrejection` with nothing rendered to catch it.
 *
 * Only chunk failures are acted on, and the same cooldown applies, so this can
 * cost at most one reload per chunk per session. Idempotent: safe to call more
 * than once.
 */
export function installChunkLoadRecovery(): void {
  if (isRecoveryInstalled) {
    return;
  }
  isRecoveryInstalled = true;

  window.addEventListener(
    "unhandledrejection",
    (event: PromiseRejectionEvent): void => {
      if (!isChunkLoadError(event.reason)) {
        return;
      }
      Log.w(TAG, "Unhandled chunk load failure", event.reason);
      attemptSilentChunkReload(event.reason);
    }
  );
}
